import type { ProductType } from "@/api_server";
import type { ProductEntryPageQuery } from "@/graphql/generated";
import { getExpandedContentBlocks, hasTypename } from "../cmsHelpers";
import type { BabyEntryContent } from "../contentTypes/entry/BabyEntryContent";
import type { CatEntryContent } from "../contentTypes/entry/CatEntryContent";
import type { ChildEntryContent } from "../contentTypes/entry/ChildEntryContent";
import type { DogEntryContent } from "../contentTypes/entry/DogEntryContent";
import type {
  DefaultEntryContent,
  EntryContentByProductType,
  ProductEntryExpandedContentBlock,
} from "../contentTypes/entry/generalEntryTypes";
import type { HouseholderEntryContent } from "../contentTypes/entry/HouseholderEntryContent";
import type { TravelEntryContent } from "../contentTypes/entry/TravelEntryContent";
import type { VanEntryContent } from "../contentTypes/entry/VanEntryContent";

const mapDefaultEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): DefaultEntryContent => ({
  common: mainContentBlocks.find(hasTypename("EntryPageCommon")),
  questions: mainContentBlocks.filter(hasTypename("QuestionBase")),
  button: mainContentBlocks.find(hasTypename("ButtonBase")),
});

const mapVanEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): VanEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    common: mainContentBlocks.find(hasTypename("EntryPageCommon")),
    registrationNumber: questions[0],
    vehicleMaker: questions[1],
  };
};

const mapDogEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): DogEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    dogName: questions[0],
    dogBreed: questions[1],
    policyHolderSsn: questions[2],
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

const mapCatEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): CatEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    catName: questions[0],
    catBreed: questions[1],
    policyHolderSsn: questions[2],
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

const mapChildEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): ChildEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    policyHolderSsn: questions[0],
    childSsn: questions[1],
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

const mapTravelEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): TravelEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    policyHolderSsn: questions[0],
    familyMemberSsn: questions[1],
    insuredParty: mainContentBlocks.find(hasTypename("RadioButtonInput")),
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

const mapHouseholderEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): HouseholderEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    policyHolderSsn: questions[0],
    housingType: questions[1],
    livingSpace: questions[2],
    housingCompany: mainContentBlocks.find(hasTypename("RadioButtonInput")),
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

const mapBabyEntryContent = (
  mainContentBlocks: ProductEntryExpandedContentBlock[],
): BabyEntryContent => {
  const questions = mainContentBlocks.filter(hasTypename("QuestionBase"));

  return {
    insuredParty: mainContentBlocks.find(hasTypename("RadioButtonInput")),
    policyHolderSsn: questions[0],
    babyMotherSsn: questions[1],
    expectedDateOfBirth: questions[2],
    submitButton: mainContentBlocks.find(hasTypename("ButtonBase")),
  };
};

export const mapEntryContentByProduct = <P extends ProductType>(
  product: P,
  entryContent: ProductEntryPageQuery,
): EntryContentByProductType<P> => {
  const mainContentBlocks =
    getExpandedContentBlocks<ProductEntryExpandedContentBlock>(
      entryContent.IfContentPage?.item?.MainContentArea,
    );

  switch (product) {
    case "van":
      return mapVanEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "dog":
      return mapDogEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "cat":
      return mapCatEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "child":
      return mapChildEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "travel":
      return mapTravelEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "householder":
      return mapHouseholderEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    case "baby":
      return mapBabyEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
    default:
      return mapDefaultEntryContent(
        mainContentBlocks,
      ) as EntryContentByProductType<P>;
  }
};
